import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  schema?: object;
  ogType?: string;
  ogImage?: string;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  schema,
  ogType = 'website',
  ogImage
}: SEOProps) => {
  const location = useLocation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://juarezmx.com';
  const fullUrl = canonical || `${siteUrl}${location.pathname}`;
  const defaultOgImage = ogImage || 'https://static.readdy.ai/image/a7a45bef519addb86f62650fa4d00e15/f12461d740e8c3a7e92fb8892e69dcde.png';

  useEffect(() => {
    // Update title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', description);
    if (keywords) updateMetaTag('keywords', keywords);
    updateMetaTag('last-modified', new Date().toISOString().split('T')[0]);

    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', fullUrl);

    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:type', ogType, true);
    updateMetaTag('og:url', fullUrl, true);
    updateMetaTag('og:site_name', 'JuarezMX', true);
    updateMetaTag('og:image', defaultOgImage, true);
    updateMetaTag('og:image:width', '1200', true);
    updateMetaTag('og:image:height', '630', true);
    updateMetaTag('og:image:alt', title, true);

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', defaultOgImage);

    // Schema.org JSON-LD
    let schemaScript = document.querySelector('script[type="application/ld+json"]');
    if (schema) {
      if (!schemaScript) {
        schemaScript = document.createElement('script');
        schemaScript.setAttribute('type', 'application/ld+json');
        document.head.appendChild(schemaScript);
      }
      schemaScript.textContent = JSON.stringify(schema);
    } else if (schemaScript) {
      schemaScript.remove();
    }

    return () => {
      // Cleanup is optional for meta tags as they'll be updated on next page
    };
  }, [title, description, keywords, fullUrl, schema, ogType, defaultOgImage]);
};

// Schema.org generators
export const generateWebSiteSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://juarezmx.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'JuarezMX',
    url: siteUrl,
    description: 'Your complete guide to Ciudad Juárez, Mexico - dining, attractions, medical tourism, and more',
    potentialAction: {
      '@type': 'SearchAction',
      target: `${siteUrl}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string'
    }
  };
};

export const generateLocalBusinessSchema = (business: {
  name: string;
  description: string;
  address: string;
  telephone?: string;
  priceRange?: string;
  servesCuisine?: string;
  image?: string;
}) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://juarezmx.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Restaurant',
    name: business.name,
    description: business.description,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Ciudad Juárez',
      addressRegion: 'Chihuahua',
      addressCountry: 'MX',
      streetAddress: business.address
    },
    telephone: business.telephone,
    priceRange: business.priceRange,
    servesCuisine: business.servesCuisine,
    image: business.image
  };
};

export const generateTouristAttractionSchema = (attraction: {
  name: string;
  description: string;
  address: string;
  image?: string;
}) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'TouristAttraction',
    name: attraction.name,
    description: attraction.description,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Ciudad Juárez',
      addressRegion: 'Chihuahua',
      addressCountry: 'MX',
      streetAddress: attraction.address
    },
    image: attraction.image
  };
};

export const generateMedicalBusinessSchema = (business: {
  name: string;
  description: string;
  address: string;
  telephone?: string;
  medicalSpecialty?: string;
}) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'MedicalBusiness',
    name: business.name,
    description: business.description,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Ciudad Juárez',
      addressRegion: 'Chihuahua',
      addressCountry: 'MX',
      streetAddress: business.address
    },
    telephone: business.telephone,
    medicalSpecialty: business.medicalSpecialty
  };
};

export const generateArticleSchema = (article: {
  headline: string;
  description: string;
  datePublished: string;
  dateModified: string;
  author?: string;
}) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://juarezmx.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.headline,
    description: article.description,
    datePublished: article.datePublished,
    dateModified: article.dateModified,
    author: {
      '@type': 'Organization',
      name: article.author || 'JuarezMX'
    },
    publisher: {
      '@type': 'Organization',
      name: 'JuarezMX',
      url: siteUrl
    }
  };
};

export const generateFAQSchema = (faqs: Array<{ question: string; answer: string }>) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };
};

export const generateWebPageSchema = (page: {
  name: string;
  description: string;
}) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://juarezmx.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: page.name,
    description: page.description,
    url: siteUrl,
    publisher: {
      '@type': 'Organization',
      name: 'JuarezMX',
      url: siteUrl
    }
  };
};
